import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, Mail } from "lucide-react";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" fill="#1877F2"/>
  </svg>
);
import { useLocalization } from "../../contexts/LocalizationContext";
import WooCommerceConnectModal from "../settings/WooCommerceConnectModal";
import styles from "./LoginForm.module.css";

import { API_BASE } from "../../utils/api/api";
import { validateWooCommerceConnection } from "../../utils/api/shopApi";
import { buildShopifyInstallUrl } from "../../utils/shopify/buildShopifyInstallUrl";

const THEME_STORAGE_KEY = "zeqi-webapp-theme";

const LoginForm = () => {
  const navigate = useNavigate();
  const { locale, t, setLocale } = useLocalization();
  const isArabic = locale === "ar";
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
    return window.matchMedia?.("(prefers-color-scheme: light)")?.matches
      ? "light"
      : "dark";
  });
  const [isWooCommerceModalOpen, setIsWooCommerceModalOpen] = useState(false);

  const heroText =
    t("shopifyConnectScreen.heroTitle") || "smarter e-commerce\npowered by AI.";
  const helperPrefix = t("shopifyConnectScreen.connectCtaSubtitle") || "";
  const helperBold = t("shopifyConnectScreen.connectCtaSubtitleBold") || "";
  const termsPrefix = t("shopifyConnectScreen.termsBlurbPrefix") || "";
  const termsLink = t("shopifyConnectScreen.termsLink") || "Terms & Conditions";
  const termsSuffix = t("shopifyConnectScreen.termsBlurbSuffix") || ".";

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = themeMode;
    document.documentElement.style.colorScheme = themeMode;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch {
      // ignore localStorage write errors
    }
  }, [themeMode]);

  const handleShopifyConnect = () => {
    window.open(buildShopifyInstallUrl(), "_blank", "noopener,noreferrer");
  };

  const handleWooCommerceConnect = () => {
    setIsWooCommerceModalOpen(true);
  };

  const handleGoogleConnect = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  const handleFacebookConnect = () => {
    window.location.href = `${API_BASE}/api/auth/facebook`;
  };

  const handleEmailConnect = () => {
    navigate("/login/email");
  };

  const handleWooCommerceConnection = async (formData) => {
    try {
      const response = await validateWooCommerceConnection({
        ...formData,
        requireWrite: false,
      });

      if (response.ok && response.data?.storeName) {
        return response;
      } else {
        throw new Error("WooCommerce connection validation failed");
      }
    } catch (error) {
      console.error("WooCommerce connection failed:", error);
      throw error;
    }
  };

  const langLabel = locale === "en" ? "ع" : "ENG";
  const supportingLabel =
    t("shopifyConnectScreen.currentlySupporting") || "Currently Supporting";

  return (
    <div className={styles.screen}>
      <div className={styles.panel}>
        <img
          src="/assets/zeqi_black_clearBG.png"
          alt="Zeqi"
          width={170}
          height={90}
          className={styles.themeAwareLogo}
        />
        <p className={`${styles.hero} ${isArabic ? styles.rtl : ""}`}>
          {heroText}
        </p>
        <p className={`${styles.helper} ${isArabic ? styles.rtl : ""}`}>
          {helperPrefix}
          {helperBold && (
            <span style={{ fontWeight: 700, marginLeft: helperPrefix ? 4 : 0 }}>
              {helperBold}
            </span>
          )}
        </p>
        <div className={styles.ctaWrapper}>
          <button
            className={styles.primary}
            onClick={handleShopifyConnect}
          >
            <img
              src="/assets/shopify_white_logo_clearBG.png"
              alt="Shopify"
              width={22}
              height={22}
            />
            {t("shopifyConnectScreen.continueWithShopify") || "Continue With Shopify"}
          </button>
          <button
            className={styles.secondary}
            onClick={handleGoogleConnect}
          >
            <GoogleIcon />
            Continue With Google
          </button>
          {/* <button
            className={styles.secondary}
            onClick={handleFacebookConnect}
          >
            <FacebookIcon />
            Continue With Facebook
          </button> */}
          <button
            className={styles.secondary}
            onClick={handleEmailConnect}
          >
            <Mail size={20} strokeWidth={2} />
            Continue With Email
          </button>
        </div>
        <p className={styles.terms}>
          {termsPrefix}
          <a href="https://zeqi.ai/terms" target="_blank" rel="noreferrer">
            {termsLink}
          </a>
          {termsSuffix}
        </p>
        {/* <div className={styles.support}>
          <span>{supportingLabel}</span>
          <div className={styles.platformLogos}>
            <img
              src="/assets/shopify_gray_clearBG.png"
              alt="Shopify"
              width={78}
              height={26}
            />
          </div>
        </div> */}
        <button
          className={styles.themePill}
          type="button"
          aria-label={
            themeMode === "dark"
              ? "Switch to light mode"
              : "Switch to dark mode"
          }
          onClick={() =>
            setThemeMode((prev) => (prev === "dark" ? "light" : "dark"))
          }
        >
          {themeMode === "dark" ? <Sun size={14} /> : <Moon size={14} />}
        </button>
        <button
          className={styles.langPill}
          onClick={() => setLocale(locale === "en" ? "ar" : "en")}
        >
          {langLabel}
        </button>
      </div>

      <WooCommerceConnectModal
        isOpen={isWooCommerceModalOpen}
        onClose={() => setIsWooCommerceModalOpen(false)}
        onConnect={handleWooCommerceConnection}
      />
    </div>
  );
};

export default LoginForm;
